import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, UserPlus, ArrowLeft, RefreshCw } from 'lucide-react';

interface AuthError {
  type: 'external_account_not_found' | 'verification_failed' | 'generic';
  message: string;
  code?: string;
}

export default function AuthErrorHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    // Check for Clerk error parameters
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const errorCode = searchParams.get('code');

    // Check URL for specific error patterns
    const url = window.location.href;
    const isNotFoundError =
      url.includes('404') ||
      url.includes('NOT_FOUND') ||
      errorCode === 'NOT_FOUND';
    const isExternalAccountError =
      errorDescription?.includes('External Account') ||
      errorDescription?.includes('not found') ||
      isNotFoundError;

    if (isExternalAccountError) {
      setError({
        type: 'external_account_not_found',
        message: t('authError.externalAccountNotFound'),
        code: errorCode || undefined,
      });
    } else if (errorParam === 'verification_failed') {
      setError({
        type: 'verification_failed',
        message: t('authError.verificationFailed'),
        code: errorCode || undefined,
      });
    } else if (errorParam || errorDescription) {
      setError({
        type: 'generic',
        message: errorDescription || t('authError.generic'),
        code: errorCode || undefined,
      });
    }
  }, [searchParams]);

  const handleSignUp = () => {
    // Navigate to sign-up page
    navigate('/sign-up', { replace: true });
  };

  const handleTryAgain = () => {
    // Navigate to sign-in page
    navigate('/sign-in', { replace: true });
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  if (!error) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
      <div className="container max-w-2xl mx-auto">
        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl md:text-3xl text-foreground">
              {t('authError.title')}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                {error.message}
              </AlertDescription>
            </Alert>

            {error.type === 'external_account_not_found' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-3">
                  {t('authError.noAccountYet')}
                </h3>
                <p className="text-blue-800 mb-4 text-sm">
                  {t('authError.trySignInDesc')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSignUp}
                    className="bg-learning-primary hover:bg-learning-primary/90"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('authError.createFreeAccount')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleTryAgain}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('authError.trySignInAgain')}
                  </Button>
                </div>
              </div>
            )}

            {error.type === 'verification_failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="font-semibold text-red-900 mb-3">
                  {t('authError.verificationIssue')}
                </h3>
                <p className="text-red-800 mb-4 text-sm">
                  {t('authError.verificationDesc')}
                </p>
                <Button
                  onClick={handleTryAgain}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            )}

            {error.type === 'generic' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {t('authError.authErrorTitle')}
                </h3>
                <p className="text-gray-800 mb-4 text-sm">
                  {t('authError.authErrorDesc')}
                </p>
                <Button
                  onClick={handleTryAgain}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            )}

            {/* Error Code Display for Debugging */}
            {error.code && (
              <div className="text-center">
                <details className="bg-muted/50 p-3 rounded border text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Technical Details
                  </summary>
                  <div className="mt-2 text-xs font-mono text-muted-foreground">
                    <p>Error Code: {error.code}</p>
                    <p>URL: {window.location.href}</p>
                  </div>
                </details>
              </div>
            )}

            {/* Navigation Options */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleGoHome}
                className="bg-white/50 backdrop-blur-sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('authError.backToHome')}
              </Button>
            </div>

            {/* Help Text */}
            <div className="text-center pt-4 text-sm text-muted-foreground">
              <p>
                {t('authError.needHelp')}{' '}
                <a
                  href="mailto:support@linguacraft.de"
                  className="text-learning-primary hover:underline"
                >
                  support@linguacraft.de
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
